/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ILinkProps } from '@fluentui/react';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, Text } from '@fluentui/react/lib/Text';
import { getFocusStyle, getTheme, mergeStyles } from '@fluentui/style-utilities';
import { Property } from 'csstype';
import format from 'date-fns/format';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { Link } from 'react-router-dom';
import { Card } from 'src/core/components/Card';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist, important } from 'src/core/themes/luisStylist';
import { routeTable } from 'src/core/utils/routeTable';
import { LuisDateFormats, getDateLocale } from 'src/core/utils/supportedLocales';
import { useCurrentVersionedAppId } from 'src/luis/modules/apps/hooks/useCurrentVersionedAppId';
import { LuisId } from 'src/luis/types/Resource';

export type PublishStatusCardProps = {
    /**
     * The publish date to be displayed
     */
    publishDate?: Date;

    /**
     * The published version id.
     */
    versionId: LuisId;

    /**
     * The slot that the specified publish was on it.
     */
    publishSlot?: string;

    /**
     * The activated external services in the published model.
     */
    externalServices?: { isSpeechPrimingOn: boolean; isSentimentAnalysisOn: boolean };

    /**
     * The published region for the published model.
     */
    regions?: string[];
};

const messages = defineMessages({
    title: {
        id: 'analytics.publishStatusCard.title',
        defaultMessage: 'Publishing status'
    },
    notPublishedYet: {
        id: 'analytics.publishStatusCard.notPublishedYet',
        defaultMessage: 'Not published yet'
    },
    publishDateLabel: {
        id: 'analytics.publishStatusCard.publishDateLabel',
        defaultMessage: 'Last published:'
    },
    versionLabel: {
        id: 'analytics.publishStatusCard.versionLabel',
        defaultMessage: 'Version:'
    },
    slotLabel: {
        id: 'analytics.publishStatusCard.slotLabel',
        defaultMessage: 'Slot:'
    },
    externalServicesLabel: {
        id: 'analytics.publishStatusCard.externalServicesLabel',
        defaultMessage: 'External services'
    },
    noServicesLabel: {
        id: 'analytics.publishStatusCard.noServicesLabel',
        defaultMessage: 'No Services'
    },
    sentimentServiceName: {
        id: 'analytics.publishStatusCard.sentimentServiceName',
        defaultMessage: 'Sentiment analysis'
    },
    speechServiceName: {
        id: 'analytics.publishStatusCard.speechServiceName',
        defaultMessage: 'Speech priming'
    },
    regionsLabel: {
        id: 'analytics.publishStatusCard.regionsLabel',
        defaultMessage: 'Regions'
    }
});

const { styleComponent, styleDiv } = getStylist('Dashboard');
const dashboardTheme = getScopedTheme('Dashboard');

const Label = styleComponent<ITextProps>(Text)<{ isBottomSpaced?: boolean }>(
    'Label',
    null,
    ({ customProps: { isBottomSpaced = true } }, theme) => ({
        lineHeight: theme.mediumLineHeight,
        minWidth: theme.publishCardLabelMinWidth,
        letterSpacing: theme.xLargeLetterSpacing,
        paddingBottom: isBottomSpaced ? theme.publishCardLinkSpacing : 0,
        fontWeight: theme.boldFont as Property.FontWeight
    })
);

const Value = styleComponent<ITextProps>(Text)<{ isDimmed: boolean }>('Value', null, ({ customProps: { isDimmed } }, theme) => ({
    display: 'block',
    opacity: isDimmed ? 0.72 : 1,
    color: theme.defaultTextColor,
    lineHeight: theme.largeLineHeight,
    letterSpacing: theme.xLargeLetterSpacing
}));

const Block = styleComponent<IStackProps>(Stack)('Block', theme => ({
    paddingBottom: theme.detailedFaultyIntentPercentLegendSpacing
}));

const StyledLink = styleComponent<ILinkProps>(Link)('StyledLink', theme => important({ color: theme.publishStatusLinksColor }));

const RegionWrapper = styleDiv('RegionWrapper', theme => ({ maxHeight: theme.regionWrapperMaxHeight, overflowY: 'auto' }));

const blockFocusStyles = mergeStyles(getFocusStyle(getTheme(), { inset: -3 }));

/**
 * The publish status card to state the last publish information such as
 * the date, the used slot, the applied external services and the target regions for this publish.
 *
 * @param props The properties of the publish status card.
 */
export const PublishStatusCard = observer((props: PublishStatusCardProps) => {
    const { publishDate, versionId, publishSlot, externalServices, regions } = props;
    const { localize } = useLocalization();
    const { uiStore } = useGlobalState();
    const { appId, versionId: currentVersionId } = useCurrentVersionedAppId();

    const displayExternalServices = (
        <>
            {externalServices && (externalServices.isSentimentAnalysisOn || externalServices.isSpeechPrimingOn) ? (
                <div data-automation-id="externalServices">
                    {externalServices.isSentimentAnalysisOn ? (
                        <Value variant="mediumPlus">{localize(messages.sentimentServiceName)}</Value>
                    ) : null}
                    {externalServices.isSpeechPrimingOn ? <Value variant="mediumPlus">{localize(messages.speechServiceName)}</Value> : null}
                </div>
            ) : (
                <Value data-automation-id="noExternalServices" customProps={{ isDimmed: true }} variant="mediumPlus">
                    {localize(messages.noServicesLabel)}
                </Value>
            )}
        </>
    );

    const displayedRegions = (
        <RegionWrapper>
            {publishDate && regions && regions.length > 0 ? (
                <div data-automation-id="regions" tabIndex={0} className={blockFocusStyles}>
                    {regions.map(r => (
                        <Value data-automation-id="regions" key={r} variant="mediumPlus">
                            {r}
                        </Value>
                    ))}
                </div>
            ) : (
                <Value data-automation-id="noRegions" customProps={{ isDimmed: true }} variant="mediumPlus">
                    {localize(messages.notPublishedYet)}
                </Value>
            )}
        </RegionWrapper>
    );

    return (
        <Card title={localize(messages.title)}>
            <Block className={blockFocusStyles}>
                <Label variant="small">{localize(messages.publishDateLabel)}</Label>

                <Value data-automation-id="publishDate" customProps={{ isDimmed: publishDate === undefined }} variant="mediumPlus">
                    {publishDate ? (
                        <Stack horizontal verticalAlign="baseline" tokens={{ childrenGap: 16 }}>
                            <LuisText size={dashboardTheme.largeFontSize} fontWeight={dashboardTheme.semiboldFont as Property.FontWeight}>
                                {format(publishDate, LuisDateFormats.Date, { locale: getDateLocale(uiStore.locale) })}
                            </LuisText>

                            <LuisText size={dashboardTheme.mediumFontSize}>
                                {publishDate ? format(publishDate, LuisDateFormats.Time, { locale: getDateLocale(uiStore.locale) }) : ''}
                            </LuisText>
                        </Stack>
                    ) : (
                        localize(messages.notPublishedYet)
                    )}
                </Value>
            </Block>

            <Block tokens={{ childrenGap: 5 }} className={blockFocusStyles}>
                <Stack horizontal verticalAlign="center">
                    <Label variant="small" customProps={{ isBottomSpaced: false }}>
                        {localize(messages.versionLabel)}
                    </Label>

                    <Text data-automation-id="version" variant="mediumPlus">
                        {versionId || '—'}
                    </Text>
                </Stack>

                <Stack horizontal verticalAlign="center">
                    <Label variant="small" customProps={{ isBottomSpaced: false }}>
                        {localize(messages.slotLabel)}
                    </Label>

                    <Text data-automation-id="slot" variant="mediumPlus">
                        {publishSlot || '—'}
                    </Text>
                </Stack>
            </Block>

            <Block className={blockFocusStyles}>
                <Label variant="small">
                    <StyledLink to={routeTable.luis.publishSettingsPage(appId, currentVersionId)}>
                        {localize(messages.externalServicesLabel)}
                    </StyledLink>
                </Label>
                {displayExternalServices}
            </Block>

            <Block className={blockFocusStyles}>
                <Label variant="small">
                    <StyledLink to={routeTable.luis.azureResourcesPage(appId, currentVersionId)}>
                        {localize(messages.regionsLabel)}
                    </StyledLink>
                </Label>

                {displayedRegions}
            </Block>
        </Card>
    );
});
